<script setup lang="ts">
import { computed, ref } from 'vue'
import axios from 'axios'
import ActionButton from '~components/widgets/ActionButton.vue'

interface SettingsRow {
  key: string
  group: string
  type: string
  value: unknown
  nullable: boolean
  _editing?: boolean
  _editValue?: string
  _error?: string
  _saving?: boolean
}

interface SnapshotRow {
  id: number
  name: string | null
  environment: string
  created_at: string
  key_count: number
}

const props = defineProps<{
  initialSettings: SettingsRow[]
}>()

const rows = ref<SettingsRow[]>(
  props.initialSettings
    .map(r => ({ ...r, _editing: false, _editValue: '', _error: '', _saving: false }))
    .sort((a, b) => a.key.localeCompare(b.key))
)

const searchQuery = ref('')

const filteredRows = computed(() => {
  const q = searchQuery.value.toLowerCase().trim()
  if (!q) return rows.value
  return rows.value.filter(r => r.key.toLowerCase().includes(q))
})

// ── Display helpers ────────────────────────────────────────────────────────

const formatDisplayValue = (row: SettingsRow): string => {
  if (row.type === 'bool') return row.value ? 'true' : 'false'
  if (row.value === null) return 'null'
  if (row.type === 'array') {
    const json = JSON.stringify(row.value)
    return json.length > 80 ? json.slice(0, 77) + '…' : json
  }
  if (row.type === 'string') return JSON.stringify(row.value)
  return String(row.value)
}

const formatEditValue = (row: SettingsRow): string => {
  if (row._editValue !== undefined && row._editValue !== '') return row._editValue
  if (row.value === null) return ''
  if (row.type === 'string') return JSON.stringify(row.value)
  return String(row.value)
}

const isExpandable = (row: SettingsRow): boolean => row.type === 'array'

// ── Inline editing (scalars) ───────────────────────────────────────────────

const startEditing = (row: SettingsRow): void => {
  if (row.type === 'bool') return
  if (isExpandable(row)) {
    toggleExpand(row)
    return
  }
  row._editing = true
  row._editValue = formatEditValue(row)
  row._error = ''
}

const onEditBlur = async (row: SettingsRow): Promise<void> => {
  const raw = row._editValue!.trim()

  if (raw === '' && row.nullable) {
    row._editing = false
    await saveSetting(row, null)
    return
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    row._error = 'Invalid JSON'
    return
  }

  const typeError = validateJsonType(parsed, row.type)
  if (typeError) {
    row._error = typeError
    return
  }

  row._editing = false
  row._error = ''
  await saveSetting(row, parsed)
}

const validateJsonType = (value: unknown, expectedType: string): string | null => {
  if (expectedType === 'bool' && typeof value !== 'boolean') return 'Expected true or false'
  if (expectedType === 'int' && (!Number.isInteger(value) || typeof value !== 'number')) return 'Expected an integer'
  if (expectedType === 'float' && typeof value !== 'number') return 'Expected a number'
  if (expectedType === 'string' && typeof value !== 'string') return 'Expected a quoted string'
  return null
}

// ── Bool toggle ────────────────────────────────────────────────────────────

const toggleBool = async (row: SettingsRow): Promise<void> => {
  const newValue = !row.value
  await saveSetting(row, newValue)
}

// ── Expandable editor (arrays) ─────────────────────────────────────────────

const toggleExpand = (row: SettingsRow): void => {
  if (!isExpandable(row)) return

  if (row._editing) {
    row._editing = false
    row._error = ''
  } else {
    row._editing = true
    row._editValue = JSON.stringify(row.value, null, 2)
    row._error = ''
  }
}

const saveExpanded = async (row: SettingsRow): Promise<void> => {
  let parsed: unknown
  try {
    parsed = JSON.parse(row._editValue!)
  } catch {
    row._error = 'Invalid JSON'
    return
  }

  await saveSetting(row, parsed)
  // Only collapse on success — saveSetting sets _error on failure
  if (!row._error) {
    row._editing = false
  }
}

const cancelExpanded = (row: SettingsRow): void => {
  row._editing = false
  row._error = ''
}

// ── Save ───────────────────────────────────────────────────────────────────

const saveSetting = async (row: SettingsRow, value: unknown): Promise<void> => {
  row._saving = true
  row._error = ''
  try {
    const resp = await axios.post('/admin/settings', { key: row.key, value })
    row.value = resp.data.value
  } catch (e: any) {
    row._error = e.response?.data?.errors?.value || e.response?.data?.error || 'Save failed'
    // Keep editor open so the user can see the error and correct
    if (row.type !== 'bool' && !isExpandable(row)) {
      row._editing = true
    }
  } finally {
    row._saving = false
  }
}

// ── Table fields ───────────────────────────────────────────────────────────

const fields = [
  { key: 'key', label: 'Key', sortable: false },
  { key: 'type', label: 'Type', sortable: false, thStyle: { maxWidth: '6em' } },
  { key: 'value', label: 'Value', sortable: false },
]

// ── Snapshots ──────────────────────────────────────────────────────────────

const showSnapshots = ref(false)
const snapshots = ref<SnapshotRow[]>([])
const snapshotName = ref('')
const snapshotSaving = ref(false)

const fetchSnapshots = async (): Promise<void> => {
  const resp = await axios.get<SnapshotRow[]>('/admin/settings/snapshots')
  snapshots.value = resp.data
}

const takeSnapshot = async (): Promise<void> => {
  snapshotSaving.value = true
  try {
    const resp = await axios.post<SnapshotRow>('/admin/settings/snapshots', {
      name: snapshotName.value.trim() || null,
    })
    snapshots.value.unshift(resp.data)
    snapshotName.value = ''
  } finally {
    snapshotSaving.value = false
  }
}

const restoreSnapshot = async (s: SnapshotRow): Promise<void> => {
  if (!confirm(`Restore all settings from snapshot "${s.name || s.created_at}"?`)) return
  await axios.post(`/admin/settings/snapshots/${s.id}/restore`)
  window.location.reload()
}

const deleteSnapshot = async (s: SnapshotRow): Promise<void> => {
  if (!confirm(`Delete snapshot "${s.name || s.created_at}"?`)) return
  await axios.delete(`/admin/settings/snapshots/${s.id}`)
  snapshots.value = snapshots.value.filter(r => r.id !== s.id)
}

const toggleSnapshots = (): void => {
  showSnapshots.value = !showSnapshots.value
  if (showSnapshots.value) fetchSnapshots()
}

defineExpose({ rows, searchQuery, filteredRows, formatDisplayValue, startEditing, toggleExpand, saveExpanded, showSnapshots, snapshots })
</script>

<template>
  <div>
    <b-row class="mb-3">
      <b-col md="6">
        <b-form-input
          v-model="searchQuery"
          type="search"
          placeholder="Filter keys"
          debounce="0"
        />
      </b-col>
    </b-row>

    <b-table
      :items="filteredRows"
      :fields="fields"
      show-empty
      empty-text="No settings found."
      striped
      hover
      small
      @row-clicked="startEditing"
    >
      <template #cell(key)="{ item }">
        <code>{{ item.key }}</code>
      </template>

      <template #cell(type)="{ item }">
        <b-badge variant="secondary">{{ item.type }}{{ item.nullable ? '?' : '' }}</b-badge>
      </template>

      <template #cell(value)="{ item }">
        <template v-if="item.type === 'bool'">
          <b-form-checkbox
            switch
            :checked="item.value === true"
            :disabled="item._saving"
            @change="toggleBool(item)"
          >
            {{ item.value ? 'true' : 'false' }}
          </b-form-checkbox>
          <small v-if="item._error" class="text-danger d-block">{{ item._error }}</small>
        </template>

        <template v-else-if="item._editing && isExpandable(item)">
          <div class="expanded-editor">
            <b-form-textarea
              v-model="item._editValue"
              rows="20"
              size="sm"
              :state="item._error ? false : null"
            />
            <small v-if="item._error" class="text-danger d-block">{{ item._error }}</small>
            <div class="mt-1">
              <action-button action="save" size="sm" :disabled="item._saving" @click.prevent.stop="saveExpanded(item)">
                Save
              </action-button>
              <b-button variant="outline-secondary" size="sm" class="ml-1" @click.stop="cancelExpanded(item)">
                Cancel
              </b-button>
            </div>
          </div>
        </template>

        <template v-else-if="isExpandable(item)">
          <code style="cursor: pointer">{{ formatDisplayValue(item) }}</code>
        </template>

        <template v-else-if="item._editing">
          <b-form-input
            v-model="item._editValue"
            size="sm"
            :state="item._error ? false : null"
            autofocus
            @blur="onEditBlur(item)"
            @keydown.escape="item._editing = false"
          />
          <small v-if="item._error" class="text-danger">{{ item._error }}</small>
        </template>

        <template v-else>
          <span
            :class="{ 'text-muted': item.value === null }"
            style="cursor: pointer"
          >
            {{ formatDisplayValue(item) }}
          </span>
        </template>
      </template>
    </b-table>

    <!-- Snapshots -->
    <hr>
    <div class="snapshots-section">
      <b-button
        variant="outline-info"
        size="sm"
        @click="toggleSnapshots"
      >
        {{ showSnapshots ? '▼' : '▶' }} Snapshots ({{ snapshots.length }})
      </b-button>

      <div v-if="showSnapshots" class="mt-3">
        <b-row class="mb-2">
          <b-col md="6">
            <b-input-group size="sm">
              <b-form-input
                v-model="snapshotName"
                placeholder="Snapshot name (optional)"
                @keydown.enter="takeSnapshot"
              />
              <b-input-group-append>
                <b-button variant="primary" :disabled="snapshotSaving" @click="takeSnapshot">
                  Take Snapshot
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
        </b-row>

        <b-table
          v-if="snapshots.length"
          :items="snapshots"
          :fields="[{key:'name',label:'Name'},{key:'environment',label:'Env'},{key:'key_count',label:'Keys'},{key:'created_at',label:'Date'},{key:'actions',label:''}]"
          small
          striped
        >
          <template #cell(name)="{ item }">
            {{ item.name || '—' }}
          </template>
          <template #cell(key_count)="{ item }">
            {{ item.key_count }} keys
          </template>
          <template #cell(created_at)="{ item }">
            {{ item.created_at.replace('T', ' ').slice(0, 19) }}
          </template>
          <template #cell(actions)="{ item }">
            <b-button-group size="sm">
              <b-button variant="primary" @click="restoreSnapshot(item)">Restore</b-button>
              <b-button variant="outline-danger" @click="deleteSnapshot(item)">Delete</b-button>
            </b-button-group>
          </template>
        </b-table>
        <p v-else class="text-muted mt-2">No snapshots yet.</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
code {
  color: #6b3030;
}
.expanded-editor {
  min-width: 300px;
}
</style>
